/**
 * InsightsHub — Role-based router (Phase 1)
 *
 * Behaviour:
 *   role === 'gtm_sales'         -> <GTMSalesInsights />
 *   role === 'gtm_sales_leader'  -> <GTMSalesLeaderInsights />
 *   role === 'cx' | 'cx_leader'  -> Phase-2 placeholder
 *   anything else                -> <LegacyInsightsHub /> (existing tab UI)
 *
 * The legacy tab UI (GTM Dashboard, CX Leader Dashboard, OTA Dashboard,
 * BFR Feedback, QC Owner, Executive Dashboard) is preserved verbatim
 * inside LegacyInsightsHub to honour the strict "do not modify unrelated
 * dashboards" rule. Only the 4 named roles see the new role-based view.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Globe2, Plus, Search, X, ChevronRight, Loader2, ExternalLink,
  CheckCircle, Clock, AlertTriangle, AlertCircle, BarChart3,
  TrendingUp, Award, Zap, RefreshCw, Target, User, Save,
  RotateCcw, Users, FileText, ShieldCheck, Hourglass,
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { otaAPI, timelineAPI, performanceAPI, leadsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import GTMDashboard from './GTMDashboard';
import CXLeaderDashboard from './CXLeaderDashboard';
import OTADashboard from './OTADashboard';
import BFRFeedbackDashboard from './BFRFeedbackDashboard';
import ExecutiveDashboard from './ExecutiveDashboard';
import QCOwnerDashboard from './QCOwnerDashboard';
import GTMSalesInsights from './insights/GTMSalesInsights';
import GTMSalesLeaderInsights from './insights/GTMSalesLeaderInsights';
import CXInsights from './insights/CXInsights';
import CXLeaderInsights from './insights/CXLeaderInsights';
import BFRInsights from './insights/BFRInsights';
import ExecutiveInsights from './insights/ExecutiveInsights';
// iter250 — QCInsights import removed. QC users now route to LegacyInsightsHub
// which filters tabs by effective permissions, so they see BFR / QC Owner /
// Onboarding Form / etc. tabs as granted by the admin permission grid.
import OnboardingFormInsights from './insights/OnboardingFormInsights';

const COLORS = ['#0d9488', '#14b8a6', '#2dd4bf', '#0e7490', '#06b6d4', '#f59e0b', '#ef4444', '#8b5cf6'];

const OTA_FIELDS = [
  { key: 'title', label: 'Title', icon: '1' },
  { key: 'description', label: 'Description', icon: '2' },
  { key: 'pricing', label: 'Pricing', icon: '3' },
  { key: 'photos', label: 'Photos', icon: '4' },
  { key: 'amenities', label: 'Amenities', icon: '5' },
  { key: 'policies', label: 'Policies', icon: '6' },
];

const STATUS_COLORS = {
  assigned: 'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  not_started: 'bg-gray-100 text-gray-600 border-gray-200',
};

// --- Role Helpers ---
const ROLE_CONFIG = {
  admin: { canAssign: true, canSeeAllOTA: true, canSeePerformance: true, canSeeSalesImpact: true, canSeeAlerts: true, canSeeReadiness: true, canSeePlatformInsights: true },
  cx_leader: { canAssign: true, canSeeAllOTA: true, canSeePerformance: true, canSeeSalesImpact: false, canSeeAlerts: true, canSeeReadiness: true, canSeePlatformInsights: true },
  cx_executive: { canAssign: false, canSeeAllOTA: false, canSeePerformance: false, canSeeSalesImpact: false, canSeeAlerts: false, canSeeReadiness: false, canSeePlatformInsights: false },
  sales_manager: { canAssign: false, canSeeAllOTA: true, canSeePerformance: true, canSeeSalesImpact: true, canSeeAlerts: true, canSeeReadiness: true, canSeePlatformInsights: true },
  sales_executive: { canAssign: false, canSeeAllOTA: false, canSeePerformance: false, canSeeSalesImpact: false, canSeeAlerts: false, canSeeReadiness: true, canSeePlatformInsights: false },
  executive: { canAssign: false, canSeeAllOTA: false, canSeePerformance: false, canSeeSalesImpact: false, canSeeAlerts: false, canSeeReadiness: false, canSeePlatformInsights: true },
};

const getRoleConfig = (role) => ROLE_CONFIG[(role || '').toLowerCase()] || ROLE_CONFIG.cx_executive;

// ════════════════════════════════════════════════════════════════════════
// Phase 2 placeholder (CX / CX Leader views ship next)
// ════════════════════════════════════════════════════════════════════════
const Phase2Placeholder = ({ role }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 flex flex-col items-center justify-center text-center" data-testid="phase2-placeholder">
    <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center mb-4">
      <Hourglass size={26} className="text-teal-600" />
    </div>
    <h2 className="text-lg font-bold text-gray-800">{role === 'cx_leader' ? 'CX Leader' : 'CX'} Insights</h2>
    <p className="text-sm text-gray-500 mt-1 max-w-md">
      Your role-based dashboard ships in Phase 2 (SLA settings, OTA assignment, timer workflow and performance tables).
    </p>
  </div>
);

// ════════════════════════════════════════════════════════════════════════
// LegacyInsightsHub — the original tab-based Insights view, lifted into
// its own component so we don't have to modify any of the underlying
// dashboard components. Used as a fallback for non-named roles.
// ════════════════════════════════════════════════════════════════════════
const SUB_TABS = [
  { key: 'dashboard',        label: 'GTM Dashboard',            icon: BarChart3,   permissionKey: 'insights_gtm' },
  { key: 'cx_leader',        label: 'CX Leader Dashboard',      icon: Users,       permissionKey: 'insights_cx' },
  { key: 'ota_dashboard',    label: 'OTA Dashboard',            icon: Globe2,      permissionKey: 'insights_ota' },
  { key: 'bfr_feedback',     label: 'BFR Feedback',             icon: FileText,    permissionKey: 'insights_bfr' },
  { key: 'qc_owner',         label: 'QC Owner',                 icon: ShieldCheck, permissionKey: 'insights_qc_owner' },
  { key: 'onboarding_form',  label: 'Onboarding Form',          icon: FileText,    permissionKey: 'insights_onboarding_form' },
  { key: 'executive',        label: 'Executive Dashboard',      icon: Award,       permissionKey: 'insights_executive' },
];

const LegacyInsightsHub = () => {
  const { user } = useAuth();
  const role = (user?.role || 'bfr').toLowerCase();
  const { canView: canViewModule, isAdmin: permIsAdmin } = usePermissions();

  const [activeTab, setActiveTab] = useState(null);

  const visibleTabs = useMemo(() =>
    SUB_TABS.filter(tab => permIsAdmin || canViewModule(tab.permissionKey)),
    [permIsAdmin, canViewModule]
  );

  // iter250 — activeTab now defaults to the first VISIBLE tab (was hard-coded
  // to 'dashboard', which caused GTMDashboard to render even for users whose
  // effective permissions granted them zero insight tabs — a regression
  // exposed by routing QC users through LegacyInsightsHub). If the user has
  // no visible tabs, activeTab stays null and no dashboard body is rendered.
  useEffect(() => {
    if (visibleTabs.length === 0) {
      if (activeTab !== null) setActiveTab(null);
      return;
    }
    if (!visibleTabs.find(t => t.key === activeTab)) {
      setActiveTab(visibleTabs[0].key);
    }
  }, [visibleTabs, activeTab]);

  const hasVisibleActive = activeTab && visibleTabs.some(t => t.key === activeTab);

  return (
    <div className="space-y-5" data-testid="insights-hub-page">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 pb-0">
          <div>
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Zap className="text-teal-600" size={24} /> Insights
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {role === 'admin' ? 'Full overview of OTA performance, team analytics & sales impact' :
               role === 'cx_executive' ? 'Your assigned OTA listings, progress & field completion' :
               role === 'sales_manager' ? 'Leads, revenue impact & OTA readiness overview' :
               role === 'sales_executive' ? 'Your leads and their OTA readiness status' :
               'Your execution tasks and OTA progress'}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0"></div>
        </div>
        <div className="flex items-center gap-1 px-5 mt-4" data-testid="insights-sub-tabs">
          {visibleTabs.map(tab => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  isActive ? 'border-teal-600 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid={`tab-${tab.key}`}>
                <TabIcon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {hasVisibleActive && activeTab === 'dashboard' && <GTMDashboard />}
      {hasVisibleActive && activeTab === 'cx_leader' && <CXLeaderDashboard />}
      {hasVisibleActive && activeTab === 'ota_dashboard' && <OTADashboard />}
      {hasVisibleActive && activeTab === 'bfr_feedback' && <BFRFeedbackDashboard />}
      {hasVisibleActive && activeTab === 'qc_owner' && <QCOwnerDashboard />}
      {hasVisibleActive && activeTab === 'onboarding_form' && <OnboardingFormInsights />}
      {hasVisibleActive && activeTab === 'executive' && <ExecutiveDashboard />}
      {visibleTabs.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center" data-testid="insights-empty-state">
          <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Zap size={26} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-800">No insight dashboards granted</h3>
          <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
            Ask your admin to grant one or more <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">insights_*</code> view
            permissions from the User Management screen to unlock this section.
          </p>
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
// Role router — the only piece exported.
// ════════════════════════════════════════════════════════════════════════
const InsightsHub = () => {
  const { user } = useAuth();
  const { canView } = usePermissions();
  // iter270 — Zero reliance on the scalar `role`. Compose from the
  // resolved roles[] set instead. Admin/executive still short-circuit
  // to the command centre; other multi-role users see EACH of their
  // role dashboards stacked (mirrors iter266 Tasks composition).
  const roles = Array.isArray(user?.roles) && user.roles.length
    ? user.roles.map(r => (r || '').toLowerCase()).filter(Boolean)
    : [(user?.role || '').toLowerCase()].filter(Boolean);
  const has = (r) => roles.includes(r);

  // Admin/Executive → single command centre (superusers).
  if (has('admin') || has('executive')) return <ExecutiveInsights />;

  // Onboarding Form standalone still wins for cx_leader-free users
  // whose ONLY relevant permission is insights_onboarding_form. When
  // the user also holds another insights-eligible role we fall through
  // to the multi-section render below so they see everything.
  const insightsRoles = [
    { role: 'cx_leader',        title: 'CX Leader Insights',    Component: CXLeaderInsights },
    { role: 'gtm_sales_leader', title: 'GTM Sales Leader Insights', Component: GTMSalesLeaderInsights },
    { role: 'gtm_sales',        title: 'GTM Sales Insights',    Component: GTMSalesInsights },
    { role: 'cx',               title: 'CX Insights',           Component: CXInsights },
    { role: 'bfr',              title: 'BFR Insights',          Component: BFRInsights },
  ];
  const active = insightsRoles.filter(s => has(s.role));

  // Onboarding Form fallback (iter250) — only when no other insights
  // role applies and the effective permission grants the view.
  if (active.length === 0 && canView('insights_onboarding_form') && !has('qc')) {
    return <OnboardingFormInsights />;
  }
  if (active.length === 0) return <LegacyInsightsHub />;
  if (active.length === 1) {
    const Only = active[0].Component;
    return <Only />;
  }
  // Multi-role user — stack every role's insights dashboard.
  return (
    <div className="space-y-8" data-testid="insights-multi-role">
      {active.map(({ role: r, title, Component }) => (
        <section key={r} className="space-y-3" data-testid={`insights-section-${r}`}>
          <header className="flex items-center gap-3 border-l-4 border-teal-500 pl-3 py-1">
            <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
            <span className="text-[11px] uppercase tracking-wider text-gray-400">role · {r}</span>
          </header>
          <Component />
        </section>
      ))}
    </div>
  );
};

export default InsightsHub;
